import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Sprout, Loader2, Check, Globe2, Mountain, Handshake, FileUp, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";

const EMPTY = { company: "", phone: "", email: "", origin_country: "", region: "", elevation: "", variety: "", quantity: "" };
const CERTS = ["Fair Trade", "Organic", "Rainforest Alliance", "Direct Trade", "UTZ", "Bird Friendly"];

export default function Suppliers() {
  const [form, setForm] = useState(EMPTY);
  const [certs, setCerts] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCert = (c) => setCerts((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Price list must be under 5 MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) return toast.error("Please enter your company name.");
    if (!form.phone.trim()) return toast.error("Please enter your contact number.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email.");
    if (!form.origin_country.trim()) return toast.error("Please enter the country of coffee origin.");
    if (!form.region.trim()) return toast.error("Please enter the region of origin.");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("certifications", certs.join(","));
      if (file) fd.append("pricelist", file);
      await axios.post(`${API_BASE}/suppliers/apply`, fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/coffee/customize" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8">
          <ArrowLeft size={16} /> Back to The Grounded Cup
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#89937C]/15 flex items-center justify-center">
            <Sprout size={18} className="text-[#89937C]" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2D372B]">Become a Partner</h1>
        </div>
        <p className="text-[#5C665A] mb-6">
          The Grounded Cup is built on exceptional beans and honest relationships. We are always on the
          lookout for passionate growers, estates and exporters — from the hills of Coorg and Chikmagalur
          to the celebrated origins of Ethiopia, Kenya, Indonesia, Vietnam, the Himalayan belt and beyond —
          who share our obsession with quality.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-10">
          {[
            { icon: Globe2, t: "Indian & International", d: "Sourcing from every origin — single estates to co-operatives." },
            { icon: Mountain, t: "Quality First", d: "High-grown lots, clean processing, traceable provenance." },
            { icon: Handshake, t: "Fair Partnerships", d: "Transparent pricing and long-term, repeat relationships." },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-[#E5E2D9] rounded-2xl p-4">
              <c.icon size={16} className="text-[#89937C] mb-2" />
              <div className="text-sm font-medium text-[#2D372B]">{c.t}</div>
              <div className="text-xs text-[#5C665A] mt-1">{c.d}</div>
            </div>
          ))}
        </div>

        {done ? (
          <div data-testid="supplier-success" className="bg-white border border-[#E5E2D9] rounded-3xl p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
              <Check size={22} className="text-[#89937C]" />
            </div>
            <h2 className="font-serif-display text-3xl text-[#2D372B] mb-2">Thank you, {form.company}!</h2>
            <p className="text-[#5C665A]">
              Your partner application has been received{file ? " along with your price list" : ""}. Coach
              Sanjay will personally connect with you at{" "}
              <span className="text-[#2D372B] font-medium">{form.email}</span> or{" "}
              <span className="text-[#2D372B] font-medium">{form.phone}</span> to discuss your coffee and the
              possibilities of working together.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#E5E2D9] rounded-3xl p-8 space-y-5">
            <div className="overline">Become a Partner — tell us about your coffee</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Company / Estate name *</label>
                <Input data-testid="supplier-company-input" value={form.company} onChange={set("company")} placeholder="e.g. Ratnagiri Estate Pvt. Ltd." className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Contact number *</label>
                <Input data-testid="supplier-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98XXXXXX00" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#5C665A] block mb-1.5">Email *</label>
              <Input data-testid="supplier-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@estate.com" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Country of coffee origin *</label>
                <Input data-testid="supplier-origin-input" value={form.origin_country} onChange={set("origin_country")} placeholder="e.g. India, Ethiopia, Colombia" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Region of origin *</label>
                <Input data-testid="supplier-region-input" value={form.region} onChange={set("region")} placeholder="e.g. Chikmagalur, Yirgacheffe, Huila" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Growing elevation <span className="opacity-60">(optional)</span></label>
                <Input data-testid="supplier-elevation-input" value={form.elevation} onChange={set("elevation")} placeholder="e.g. 1,200–1,500 masl" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
              <div>
                <label className="text-xs text-[#5C665A] block mb-1.5">Variety <span className="opacity-60">(optional)</span></label>
                <Input data-testid="supplier-variety-input" value={form.variety} onChange={set("variety")} placeholder="e.g. SL9, Kent, Geisha, Catimor" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#5C665A] block mb-1.5">Quantity / batch you can supply <span className="opacity-60">(optional)</span></label>
              <Input data-testid="supplier-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 500 kg per harvest" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            </div>

            <div>
              <label className="text-xs text-[#5C665A] block mb-2">Certifications <span className="opacity-60">(select all that apply)</span></label>
              <div className="flex flex-wrap gap-2">
                {CERTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`supplier-cert-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleCert(c)}
                    className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                      certs.includes(c)
                        ? "border-[#89937C] bg-[#89937C]/10 text-[#2D372B] font-medium"
                        : "border-[#E5E2D9] text-[#5C665A] hover:border-[#89937C]/60"
                    }`}
                  >
                    {certs.includes(c) && <Check size={12} className="inline mr-1 -mt-0.5" />}
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-[#5C665A] block mb-2">Wholesale price list <span className="opacity-60">(PDF, Excel, CSV or image · max 5 MB)</span></label>
              <input ref={fileRef} type="file" accept=".pdf,.csv,.xls,.xlsx,.png,.jpg,.jpeg,.doc,.docx" onChange={onFile} className="hidden" data-testid="supplier-pricelist-input" />
              {file ? (
                <div className="flex items-center justify-between gap-3 bg-[#F7F5F0] border border-[#E5E2D9] rounded-2xl px-4 py-3">
                  <span data-testid="supplier-pricelist-name" className="text-sm text-[#2D372B] truncate">{file.name}</span>
                  <button type="button" data-testid="supplier-pricelist-remove" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#5C665A] hover:text-[#2D372B]">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="supplier-pricelist-btn"
                  onClick={() => fileRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-4 border border-dashed border-[#89937C]/50 text-sm text-[#5C665A] hover:bg-[#89937C]/5 transition-colors"
                >
                  <FileUp size={16} /> Attach your wholesale price list
                </button>
              )}
            </div>

            <button
              data-testid="supplier-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Submit partner application
            </button>
            <p className="text-xs text-[#5C665A] text-center">
              We review every application personally and respond within 48 hours.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
